import {
    AnimationBaseController,
    IAnimationControllerConfiguration,
    Inject,
    ModelEvents,
    SoundType,
} from '@denishussar/html5-game-engine';
import {ButtonController, IButtonControllerOptions,} from '../../../Common/ButtonController';
import {GameLogic} from '../../../Logic/GameLogic';
import {BoosterTypeEnum, GameModel} from '../../../Models/GameModel';
import {ModalsNames} from '../../../Common/MadalsManager/ModalsList';
import {LocalizationContents} from '../../../LocalizationContents';
import {ConfirmationsYouRewardType} from '../../Modals/Confirmations/ConfirmationsYouRewardController';
import {CrazyGamesAdsLogic} from '../../../Logic/CrazyGames/CrazyGamesAdsLogic';
import {
    GameEvent,
    GameEventPopupButtonName,
    GameEventPopupName,
    GameEventSceneButtonClick, GameEventSceneName,
} from '../../../Constants/GameEvent';
import {AdsLogic} from '../../../Logic/Ads/AdsLogic';
import * as filter from '@pixi/filter-glow';
import gsap from "gsap";
import {IPictureData} from "../../../Services/CloudDB/Contract";
import {IPictureTexture} from "../Catalog/CatalogFolderController";
import {getSpriteFromUrl} from "../../../Common/getSpriteFromUrl";
import {Route} from "../../../Utils/Helpers";

export interface UIDailyRewardControllerOptions {
}

export enum UIDailyRewardType {
    COINS,
    PICTURE_OR_COINS,
    PICTURE,
    BOOSTER_WOOL,
    BOOSTER_NEEDLE,
    BOOSTER_COMBO
}

export interface UIDailyRewardProps {
    type: UIDailyRewardType;
    id?: string;
    size?: number;
    count?: number | number[];
    coins?: number;
}

export class UIDailyRewardController extends AnimationBaseController {
    static REWARD_DAYS: number = 7;
    static REWARD_COOLDOWN: number = 24 * 60 * 60 * 1000;

    private rewardConfig: UIDailyRewardProps[] = [];

    $model: GameModel;

    private timerId;
    private buttonAdd: ButtonController;
    private buttonAddTime: ButtonController;
    private filterGlow?: filter.GlowFilter;

    private static isCheatDailyRewardApplied = false;

    public constructor(
        @Inject private options: UIDailyRewardControllerOptions,
        @Inject private gameLogic: GameLogic,
        @Inject private crazyGamesAdsLogic: CrazyGamesAdsLogic,
        @Inject private modelEvents: ModelEvents,
        @Inject private model: GameModel,
        @Inject private adsLogic: AdsLogic
    ) {
        super('UIDailyRewardController');

        const cheatDailyReward = Route.searchParam('cheatDailyReward');
        if (cheatDailyReward === '1' || cheatDailyReward === 'true') {
            if (!UIDailyRewardController.isCheatDailyRewardApplied) {
                UIDailyRewardController.isCheatDailyRewardApplied = true;
                UIDailyRewardController.REWARD_COOLDOWN = 3 * 1000;
                this.gameLogic.addsCooldownUpdate(0);
            }
        }
    }

    protected getConfiguration(): IAnimationControllerConfiguration {
        const onClick = (status: boolean) => {
            this.modelEvents.dispatch(this.model, GameEvent.sceneButtonClick, <GameEventSceneButtonClick>{
                buttonName: 'dailyPrize',
                status
            });

            if (!status) {
                return;
            }

            if (this.filterGlow) {
                gsap.killTweensOf(this.filterGlow);
                this.buttonAdd.display.filters = [];
            }

            this.gameLogic.modalsManager.show(ModalsNames.dailyReward, {
                day: this.model.addsDay,
                rewards: this.rewardConfig,
                closeClick: () => this.gameLogic.modalsManager.hide(),
                claimClick: async (reward: UIDailyRewardProps) => {
                    this.gameLogic.modalsManager.hide();

                    this.showAdvertisment()
                        .then(async () => {
                            const day = this.model.addsDay;
                            const type = this.getRewardTypeFromDailyReward(reward.type);
                            const value = type === ConfirmationsYouRewardType.picture ? `${reward.size}x${reward.size}` : reward.count;
                            const texture = type === ConfirmationsYouRewardType.picture ?
                                await this.getTextureFromPicture(this.model.albumLibrary.getPictureData(reward.id)) : undefined;

                            this.modelEvents.dispatch(this.model, GameEvent.dailyReward, {
                                sceneName: GameEventSceneName.lobby,
                                popupName: GameEventPopupName.dailyPrize,
                                day: (day + 1),
                                type,
                            });

                            this.gameLogic.modalsManager.show(ModalsNames.youReward, {
                                    type,
                                    value,
                                    texture,
                                    text: LocalizationContents.getText('ReceiveGiftTitle'),
                                    okText: LocalizationContents.getText('Thanks'),
                                    okClick: () => {
                                        this.claimReward();
                                        this.showTimer();
                                        this.updateReward();

                                        this.gameLogic.modalsManager.hide();
                                    },
                                    exitClick: () => {
                                        this.claimReward();
                                        this.showTimer();
                                        this.updateReward();

                                        this.gameLogic.modalsManager.hide();
                                    },
                                }
                            );
                        })
                        .catch(e => this.showError());
                }
            });
        };

        return {
            childControllers: {
                button_add: {
                    instanceName: 'btn_add',
                    controller: ButtonController,
                    options: <IButtonControllerOptions>{
                        label: LocalizationContents.getText('View Ads'),
                        onClick: onClick,
                    }
                },
                button_add_time: {
                    instanceName: 'btn_add_time',
                    controller: ButtonController,
                    options: <IButtonControllerOptions>{
                        label: LocalizationContents.getText('View Ads'),
                        style: {
                            align: 'center'
                        },
                        onClick: onClick,
                    }
                },
            }
        };
    }

    protected onInitialize(): void {
        this.updateReward();

        this.buttonAdd = this.$childControllers.get('button_add') as ButtonController;
        this.buttonAddTime = this.$childControllers.get('button_add_time') as ButtonController;

        if (this.isRewardAvailable()) {
            this.filterGlow = new filter.GlowFilter(50, 0, 0);
            this.buttonAdd.display.filters = [this.filterGlow];

            gsap.killTweensOf(this.filterGlow);
            gsap.to(this.filterGlow, {
                duration: .5,
                innerStrength: 4,
                yoyo: true,
                yoyoEase: true,
                repeat: -1,
            });

            this.buttonAdd.visible = true;
            this.buttonAddTime.visible = false;

        } else {
            this.buttonAdd.visible = false;
            this.buttonAddTime.visible = true;

            this.showTimer();
        }
    }

    protected onDestroy(): void {
        clearTimeout(this.timerId);
    }

    private getRewardTypeFromDailyReward(value: UIDailyRewardType): ConfirmationsYouRewardType {
        switch (value) {
            case UIDailyRewardType.BOOSTER_WOOL:
                return ConfirmationsYouRewardType.yarn;

            case UIDailyRewardType.BOOSTER_NEEDLE:
                return ConfirmationsYouRewardType.needle;

            case UIDailyRewardType.BOOSTER_COMBO:
                return ConfirmationsYouRewardType.combo;

            case UIDailyRewardType.PICTURE:
                return ConfirmationsYouRewardType.picture;
        }

        return ConfirmationsYouRewardType.money;
    }

    private updateReward(): void {
        this.rewardConfig = this.calculateReward();
    }

    private calculateReward(): UIDailyRewardProps[] {
        const defaultReward = [
            {type: UIDailyRewardType.BOOSTER_NEEDLE, count: 1},
            {type: UIDailyRewardType.BOOSTER_WOOL, count: 1},
            {type: UIDailyRewardType.BOOSTER_COMBO, count: [1, 1]},
            {type: UIDailyRewardType.PICTURE_OR_COINS, size: 50, coins: 50},
            {type: UIDailyRewardType.BOOSTER_COMBO, count: [2, 3]},
            {type: UIDailyRewardType.COINS, count: 70},
            {type: UIDailyRewardType.PICTURE_OR_COINS, size: 70, coins: 70}
        ];

        const resultReward = [];

        for (let i = 0; i < defaultReward.length; i++) {
            const reward = defaultReward[i];

            if (reward.type === UIDailyRewardType.PICTURE_OR_COINS) {
                const picture = this.model.albumLibrary.getRandomPictureBySize(reward.size);

                if (picture) {
                    resultReward.push({...reward, type: UIDailyRewardType.PICTURE, id: picture.id});

                } else {
                    resultReward.push({...reward, type: UIDailyRewardType.COINS, count: reward.coins});
                }

            } else {
                resultReward.push(reward);
            }
        }

        return resultReward;
    }

    private claimReward(): UIDailyRewardType {
        const currentDay = this.model.addsDay;
        const totalDays = UIDailyRewardController.REWARD_DAYS;

        const rewardConfig = this.rewardConfig[currentDay];
        const {type, count, id, coins} = rewardConfig;

        switch (type) {
            case UIDailyRewardType.COINS:
                this.gameLogic.addBalanceCoins(coins);
                break;

            case UIDailyRewardType.BOOSTER_WOOL:
                this.gameLogic.addBooster(BoosterTypeEnum.yarn, count as number);
                break;

            case UIDailyRewardType.BOOSTER_NEEDLE:
                this.gameLogic.addBooster(BoosterTypeEnum.needle, count as number);
                break;

            case UIDailyRewardType.BOOSTER_COMBO:
                const countA = Array.isArray(count) ? count[0] : count;
                const countB = Array.isArray(count) ? count[1] : count;

                this.gameLogic.addBooster(BoosterTypeEnum.yarn, countA);
                this.gameLogic.addBooster(BoosterTypeEnum.needle, countB);
                break;

            case UIDailyRewardType.PICTURE:
                this.gameLogic.setPicture(this.model.albumLibrary.getPictureData(id), {isSetPictureForAds: true});
                break;
        }

        this.gameLogic.addsCooldownUpdate(currentDay === (totalDays - 1) ? 0 : currentDay + 1);

        return type;
    }

    private isRewardAvailable(): boolean {
        return this.model.addsDay === 0 ? true : Date.now() - this.$model.addsCooldownTimestamp >= UIDailyRewardController.REWARD_COOLDOWN;
    }

    private showTimer(): void {
        this.buttonAdd.visible = false;
        this.buttonAddTime.visible = true;

        clearTimeout(this.timerId);

        this.timerUpdate();
    }

    private hideTimer(): void {
        this.buttonAdd.visible = true;
        this.buttonAddTime.visible = false;

        clearTimeout(this.timerId);
    }

    update(frameRate: number): boolean {
        const text = this.buttonAddTime.text.children[3] as any;
        text.anchor.set(0.5, 0);
        text.x = -4;
        text.style.align = 'center';

        return super.update(frameRate);
    }

    private timerUpdate(): void {
        const time = Date.now() - this.$model.addsCooldownTimestamp;

        if (time > UIDailyRewardController.REWARD_COOLDOWN) {
            this.hideTimer();
            return;
        }

        const date = new Date(UIDailyRewardController.REWARD_COOLDOWN - time);
        const hours = date.getUTCHours();
        const minutes = date.getMinutes();
        const seconds = date.getSeconds();

        this.buttonAddTime.label = `${hours}:${minutes < 10 ? '0' + minutes : minutes}:${seconds < 10 ? '0' + seconds : seconds}`;

        this.timerId = setTimeout(() => this.timerUpdate(), 1000);
    }

    private showError(): Promise<void> {
        return new Promise((resolve, reject) => {
            this.gameLogic.modalsManager.show(ModalsNames.confirm, {
                oneButtonMode: true,
                text: LocalizationContents.getText('Something wrong with ads'),
                yesText: LocalizationContents.getText('Ok'),
                yesClick: () => {
                    resolve();
                    this.gameLogic.modalsManager.hide();
                },
            });
        });
    }

    private async showAdvertisment(): Promise<void> {
        if (!this.adsLogic.isReady) {
            return Promise.reject("Ads no ready");
        }

        this.soundManager.muteSoundsByType(true, SoundType.ALL);

        return this.adsLogic.adsLogic.requestAd().finally(() => this.soundManager.muteSoundsByType(false, SoundType.ALL));
    }

    private async getTextureFromPicture(picture: IPictureData): Promise<IPictureTexture> {
        const save = this.gameLogic.getSave(picture.id);

        if (save && save.src) {
            return new Promise((resolve) => {
                const texture = PIXI.Texture.from(save.src);

                const timeout = 100;
                const size = 1;
                let timerId = setTimeout(function tick() {
                    if (texture.width > size && texture.height > size) {
                        texture.baseTexture.scaleMode = PIXI.SCALE_MODES.NEAREST;
                        resolve({picture, texture});
                    } else {
                        timerId = setTimeout(tick, timeout);
                    }
                }, timeout);
            });
        }


        return getSpriteFromUrl('assets/' + picture.src, `icon${picture.id}`)
            .then((res) => {
                const {sprite, cached} = res;
                let texture: PIXI.Texture | undefined;
                if (sprite.texture) {
                    texture = sprite.texture;
                    texture.baseTexture.scaleMode = PIXI.SCALE_MODES.NEAREST;
                }

                return Promise.resolve({picture, texture});
            });
    }
}
